import React, { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { KeyboardAvoidingView, KeyboardEvents } from 'react-native-keyboard-controller';

import { IllusDressing_1 } from '@garnish/core';
import { HostedFrame, type HostedFrameRef } from '@garnish/hosted-frame';

import type { OrderType } from '@sg/graphql-schema';

import { LoadingAnimation } from '../../components';
import { useMomoSurveyMessages } from './hooks';
import { useRateOrder } from './RateOrderScreen.data';
import { LandingContent } from './RateOrderScreen.landing';

// ─────────────────────────────────────────────────────────────────────────────

export const MomosSurveyContent = (props: MomosSurveyContentProps) => {
  const { url, orderId, orderType } = props;

  const { formatMessage } = useIntl();

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const frameRef = useRef<HostedFrameRef>(null);

  // ─── State ─────────────────────────────────────────────────────────────────

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const { handleSubmit } = useRateOrder();

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const { onMessage } = useMomoSurveyMessages({
    url,
    orderId,
    orderType,
    handleRatingSubmission: handleSubmit,
  });

  const handleLoadEnd = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  const handleKeyboardDidShow = useCallback(() => {
    const frame = frameRef.current?.frame;
    const canInjectJavaScript = frame && 'injectJavaScript' in frame;

    if (!canInjectJavaScript) return;

    frame.injectJavaScript(SCROLL_TO_FOCUSED_INPUT_SCRIPT);
  }, []);

  const handleKeyboardSubscription = useCallback(() => {
    const keyboardSubscription = KeyboardEvents.addListener(
      'keyboardDidShow',
      handleKeyboardDidShow,
    );

    return () => {
      keyboardSubscription.remove();
    };
  }, [handleKeyboardDidShow]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(handleKeyboardSubscription, [handleKeyboardSubscription]);

  // ───────────────────────────────────────────────────────────────────────────

  if (hasError) {
    return (
      <LandingContent>
        <LandingContent.Illustration source={IllusDressing_1} style={styles.errorIllustration} />

        <LandingContent.Title>{formatMessage(messages.errorTitle)}</LandingContent.Title>

        <LandingContent.Subtitle>{formatMessage(messages.errorSubtitle)}</LandingContent.Subtitle>
      </LandingContent>
    );
  }

  return (
    <KeyboardAvoidingView style={styles.container} behavior="height" automaticOffset>
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <LoadingAnimation />
        </View>
      )}

      <HostedFrame
        ref={frameRef}
        frameId="momos-rate-order-survey"
        frameHeight="100%"
        containerStyle={styles.frameContainer}
        source={{ uri: url }}
        isLoading={isLoading}
        onLoadEnd={handleLoadEnd}
        onError={handleError}
        onMessage={onMessage}
        acceptUnmarkedMessages
        allowPopupsToEscapeSandbox
        sharedCookiesEnabled
        thirdPartyCookiesEnabled
      />
    </KeyboardAvoidingView>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

/**
 * NOTE: The trailing `true` gives `react-native-webview` a serializable result and
 *       prevents injected JavaScript from silently failing on iOS.
 */
const SCROLL_TO_FOCUSED_INPUT_SCRIPT = `
  requestAnimationFrame(() => {
    document.activeElement?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  });
  true;
`;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  errorTitle: {
    defaultMessage: `We couldn't load this survey.`,
    description: 'Rate | Order | Momos Survey | Error View | Title',
  },
  errorSubtitle: {
    defaultMessage: 'Please try again later.',
    description: 'Rate | Order | Momos Survey | Error View | Subtitle',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
  },
  frameContainer: {
    flex: 1,
    height: '100%',
    width: '100%',
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFill,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorIllustration: {
    width: 200,
    height: 200,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type MomosSurveyContentProps = Readonly<{
  url: string;
  orderId: string;
  orderType: OrderType;
}>;
